import { SxProp } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';

import {
  EmptyResultTypeEnum,
  FriendshipUserList as BaseFriendshipUserList,
} from '@components';
import {
  MainNavigatorNavigationPropType,
  MusicNavigatorNavigationPropType,
  PersonalizationNavigatorNavigationPropType,
  screens,
} from '@navigation';
import type { User } from '@models/Viewer/UserModel';
import type { FriendshipUserList } from '@models/Viewer/FriendshipUserListModel';

function contentContainerStyle(props: EdgeInsets): SxProp {
  return {
    flexGrow: 1,
    px: ['$6', '$0'],
    pb: props.bottom,
  };
}

interface IFollowingListProps {
  list: FriendshipUserList;
  fetchMore(): Promise<void>;
  refresh(): Promise<void>;
}
type NavigationPropType = CompositeNavigationProp<
  CompositeNavigationProp<
    MusicNavigatorNavigationPropType<'Following'>,
    MainNavigatorNavigationPropType<'Following'>
  >,
  PersonalizationNavigatorNavigationPropType<'Following'>
>;

export const FollowingList = observer<IFollowingListProps>(
  ({ list, fetchMore, refresh }) => {
    const insets = useSafeAreaInsets();
    const nav = useNavigation<NavigationPropType>();

    const navigateToUser = useCallback((item: User) => {
      nav.push(screens.UserProfileTabNavigator, {
        id: item.id,
      });
    }, []);

    const isLoading = list.fetch.inProgress && list.isEmpty;

    let emptyResultType: EmptyResultTypeEnum =
      EmptyResultTypeEnum.Error;

    if (list.fetch.isError) {
      emptyResultType = EmptyResultTypeEnum.Error;
    } else if (list.isEmpty) {
      emptyResultType = EmptyResultTypeEnum.NoFollowing;
    }

    return (
      <BaseFriendshipUserList
        contentContainerSx={contentContainerStyle(insets)}
        data={list.asArray}
        onEndReached={fetchMore}
        onRefresh={refresh}
        isRefreshing={list.fetch.inProgress}
        isLoading={isLoading}
        isLoadingMore={list.fetchMore.inProgress}
        onPress={navigateToUser}
        emptyResultType={emptyResultType}
      />
    );
  },
);
